<template>
  <v-container fluid pa-0>
    <v-card tile flat>
      <v-card-title>
        <v-menu
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              rounded
              single-line
              dense
              filled
              hide-details
              label="Datum"
              v-model="filter.date"
              v-on="on"
              v-bind="attrs"
              readonly
              prepend-inner-icon="mdi-calendar-range"
              class="mr-4"
            ></v-text-field>
          </template>
          <v-date-picker
            first-day-of-week="1"
            v-model="filter.date"
          ></v-date-picker>
        </v-menu>

        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Sök..."
          rounded
          single-line
          dense
          filled
          hide-details
        ></v-text-field>
        <v-spacer />
        <v-btn
          @click="exportExcel"
          rounded
          :disabled="!data.length"
          :loading="exporting"
          class="mr-2"
        >
          <v-icon left small>mdi-file-export-outline</v-icon>
          Export Excel</v-btn
        >
        <v-dialog v-model="showCreateModal" width="1000" persistent scrollable>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" rounded color="primary">
              <v-icon small class="pr-4">mdi-plus-circle-outline</v-icon>
              Nytt ärende
            </v-btn>
          </template>
          <OdocCreateModal
            v-if="showCreateModal"
            @close="showCreateModal = false"
            @reload="fetchDataOnly"
            :locations="odocLocations"
            :case-types="odocCaseTypes"
          />
        </v-dialog>
        <v-dialog v-model="showEditModal" width="1000" persistent scrollable>
          <OdocEditModal
            v-if="showEditModal"
            @close="showEditModal = false"
            :locations="odocLocations"
            :case-types="odocCaseTypes"
          />
        </v-dialog>
      </v-card-title>
      <v-data-table
        :custom-sort="customSort"
        :loading="loading"
        :headers="headers"
        :items="data"
        :search="search"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :item-class="
          item => (item.source === 'PROPOSAL' ? $style.proposalRow : '')
        "
        :options="{
          itemsPerPage: 50
        }"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50, -1]
        }"
        fixed-header
        :height="tableHeight"
        @click:row="onRowClick"
      >
        <template v-slot:item.source="{ item }">
          <template v-if="item.source === 'PROPOSAL'">
            <v-icon small color="primary">mdi-circle-edit-outline</v-icon>
          </template>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import inputTableHeight from '@/mixins/inputTableHeight'
import OdocCreateModal from './OdocCreateModal'
import OdocEditModal from './OdocEditModal'
import GET_ODOC_STATUSES from '../graphql/getOdocStatuses.graphql'
import GET_ODOC_LOCATIONS from '../graphql/getOdocLocations.graphql'
import GET_ODOC_CASE_TYPES from '../graphql/getOdocCaseTypes.graphql'
import orderBy from 'lodash.orderby'
import { format } from 'date-fns'
import { get } from 'vuex-pathify'
import { exportExcel } from '@/services/exportExcel.service'

const headers = [
  {
    text: '',
    value: 'source',
    width: 70
  },
  {
    text: 'Status',
    value: '_status'
  },
  {
    text: 'Ärendenr.',
    value: 'identifier'
  },
  {
    text: 'Skapad',
    value: '_created_date',
    width: 150
  },
  {
    text: 'Skapad av',
    value: 'created_by_name'
  },
  {
    text: 'Händelsedatum',
    value: '_event_date',
    width: 150
  },
  {
    text: 'Rubrik',
    value: 'name'
  },
  {
    text: 'Beskrivning',
    value: 'description'
  },

  {
    text: 'Operatörsberoende',
    value: '_operators_beroende'
  },

  {
    text: 'Tågnummer',
    value: '_tur'
  },
  {
    text: 'Sträcka',
    value: '_sammanfattning'
  }
]

export default {
  name: 'OdocTable',

  components: {
    OdocCreateModal,
    OdocEditModal
  },

  mixins: [inputTableHeight(187)],

  watch: {
    'filter.date': 'fetchDataOnly'
  },

  created() {
    this.fetchAll()
  },

  data() {
    return {
      showCreateModal: false,
      showEditModal: false,
      headers,
      search: '',
      sortDesc: true,
      sortBy: 'created_date',
      filter: {
        date: format(new Date(), 'yyyy-MM-dd')
      },
      loading: false,
      exporting: false,
      odocStatuses: [],
      odocLocations: [],
      odocCaseTypes: []
    }
  },

  computed: {
    odocCases: get('odoc/cases'),
    data() {
      const data = this.odocCases.map(x => {
        const statusName = this.odocStatuses?.find(
          y => y.status_guid === x.status_guid
        )?.name
        return {
          ...x,
          _status:
            x.source === 'PROPOSAL' && statusName === 'Inkommen'
              ? 'Förslag'
              : statusName,
          _created_date: x.created_date
            ? format(new Date(x.created_date), 'yyyy-MM-dd HH:mm')
            : null,
          _event_date: format(new Date(x.event_date), 'yyyy-MM-dd HH:mm'),
          _operators_beroende:
            x?.custom_properties?.find(y => y.name === 'OG_Operatorsberoende')
              ?.value || null,
          _tur:
            x?.custom_properties?.find(y => y.name === 'OGTH_Tur')?.value ||
            null,
          _sammanfattning:
            x?.custom_properties?.find(y => y.name === 'OGTH_Sammanfattning')
              ?.value || null,
          _prognosis:
            x?.custom_properties?.find(y => y.name === 'OGTH_Prognosis')
              ?.value || null
        }
      })
      return orderBy(data, ['created_date'], ['asc'])
    }
  },

  methods: {
    customSort(items, sortBy, sortDesc) {
      return orderBy(
        items,
        ['source', ...sortBy],
        ['desc', ...sortDesc.map(x => (x ? 'desc' : 'asc'))]
      )
    },
    fetch() {
      return this.$store.dispatch('odoc/fetchCases', this.filter.date)
    },
    onRowClick(item) {
      this.$store.dispatch('odoc/editModel', item)
      this.$store.set('odoc/selectedCase', item._uuid)
      if (item.source === 'PROPOSAL') {
        this.showCreateModal = true
      } else {
        this.showEditModal = true
      }
    },

    async fetchSubdata() {
      const {
        data: { odocStatuses }
      } = await this.$apollo.query({
        query: GET_ODOC_STATUSES
      })
      const {
        data: { odocLocations }
      } = await this.$apollo.query({
        query: GET_ODOC_LOCATIONS
      })
      const {
        data: { odocCaseTypes }
      } = await this.$apollo.query({
        query: GET_ODOC_CASE_TYPES
      })
      this.odocStatuses = odocStatuses
      this.odocLocations = odocLocations
      this.odocCaseTypes = odocCaseTypes
    },

    async fetchAll() {
      this.loading = true
      await this.fetchSubdata()
      await this.fetch()
      this.loading = false
    },

    async fetchDataOnly() {
      this.loading = true
      await this.fetch()
      this.loading = false
    },

    async exportExcel() {
      this.exporting = true

      const pluckKeys = x => {
        return Object.values(this.headers.slice(1)).reduce(
          (acc, { text, value }) => {
            acc[text] = x[value]
            return acc
          },
          {}
        )
      }

      await exportExcel({
        name: `ödoc`,
        sheets: [
          {
            name: 'ödoc',
            data: this.data.map(pluckKeys)
          }
        ]
      })

      this.exporting = false
    }
  }
}
</script>

<style lang="scss" module>
.proposalRow {
  background: rgba(blue, 0.08);
}
</style>
